import { Box, Stack, TextField, Tooltip } from "@mui/material"
import { MutateOptions } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useFindAcquisitionModes, useFindAcquisitionModesForFlightPlan } from "../../api/AcquisitionModeApi"
import {
    FlightPlannedLineCreateDto,
    useCreateFlightplan,
    useDeleteFlightplan,
    useUpdateFlightplan,
} from "../../api/FlightplansApi"
import { NexAutocomplete } from "../../components/Fields/Autocomplete"
import { NexSelect } from "../../components/Fields/NexSelect"
import { LoadingButton } from "../../components/LoadingButton/LoadingButton"
import { AlertModal } from "../../components/Modals/AlertModal"
import { StepperForm } from "../../components/StepperForm/StepperForm"
import { ProjectTitle } from "../../components/Titles/ProjectTitle"
import { Label } from "../../components/Typography/Typography"
import { useNexusNavigate } from "../../hooks/useNexusNavigate"
import { useNexusFlightPlanIdOptional, useNexusFlightplanFlow, useNexusProjectNumber } from "../../hooks/useNexusParams"
import { useTitle } from "../../hooks/useTitle"
import { ConfirmationRoutes, routes } from "../../routing/NexRoutes"
import { ToolbarOptions } from "../../store/types/AuthTypes"
import { FlightplanStatus, FlightplanType } from "../../store/types/FlightPlansTypes"
import { AcquisitionPlatform } from "../../store/types/MissionsTypes"
import { keysForStringEnum } from "../../utils/EnumUtils"
import { StringUtils } from "../../utils/StringUtils"
import { isRowInserted } from "../../utils/TableUtils"
import { useAuthStore } from "../../zstore/AuthStore"
import { useFlightplanEditStore } from "../../zstore/FlightplanEditStore"
import { FlightplanCompletedLines } from "./FlightplanCompletedLines"
import { FlightplanEditorSelectAcquisitionModes } from "./FlightplanEditorSelectAcquisitionModes"
import { useFlightplanEditorStyles } from "./FlightplanEditorStyles"
import { FlightplanStatusSelect } from "./FlightplanStatusSelect"
import { FlightplanTable } from "./FlightplanTable"
import { getFlightplanValidatorModel } from "./model"

export const FlightplanEditor = () => {
    useTitle("Flightplan Editor")
    const navigate = useNavigate()
    const { classes, cx } = useFlightplanEditorStyles()
    const flightplanFlow = useNexusFlightplanFlow()
    const flightPlanId = useNexusFlightPlanIdOptional()
    const projectNumber = useNexusProjectNumber()

    const flightPlanStoreFetchError = useFlightplanEditStore((state) => state.fetchError)
    const flightPlanStoreClearError = useFlightplanEditStore((state) => state.clearError)
    const fetchFlightplansForProject = useFlightplanEditStore((state) => state.fetchFlightplansForProject)
    const selectFlightplanForProject = useFlightplanEditStore((state) => state.selectFlightplanForProject)
    const selectedFlightplan = useFlightplanEditStore((state) => state.selectedFlightplan)
    const flightplansForProject = useFlightplanEditStore((state) => state.flightplansForProject)
    const flightPlannedLines = useFlightplanEditStore((state) => state.selectedFlightplanPlannedLines)
    const deletedPlannedLineIds = useFlightplanEditStore((state) => state.deletedPlannedLineIds)
    const resetFlightplansStore = useFlightplanEditStore((state) => state.reset)
    const fetchingFlightplans = useFlightplanEditStore((state) => state.fetchingFlightplans)
    const selectingFlightplan = useFlightplanEditStore((state) => state.selectingFlightplan)
    const parsingFile = useFlightplanEditStore((state) => state.parsingFile)
    const updateFlightplanField = useFlightplanEditStore((state) => state.updateFlightplanField)
    const warnings = useFlightplanEditStore((state) => state.warnings)

    const checkToolbarAccess = useAuthStore((state) => state.checkToolbarAccess)
    const [error, setError] = useState<string | undefined>()

    const flightplanIdNum = flightPlanId == null ? selectedFlightplan.flight_plan_id : Number(flightPlanId)

    // Ideally we would use react-query to load just the Project, and the FlightPlan + related data in two
    // backend requests. However, it would be difficult to rewrite this entire component to use react-query and not
    // use Redux at all, so for now we are loading acquisition modes separately with react-query.
    const acquisitionModesResponse = useFindAcquisitionModes()
    const acquisitionModesForFlightPlanResponse = useFindAcquisitionModesForFlightPlan(flightplanIdNum)

    const createFlightplan = useCreateFlightplan()
    const updateFlightplan = useUpdateFlightplan()
    const deleteFlightplan = useDeleteFlightplan()
    const nexusNavigate = useNexusNavigate()

    const isEditFlow = flightplanFlow === "edit"
    const isFlightplanEmpty = isEditFlow && flightplansForProject.length === 0

    let isDeleteDisabled = false
    let deleteTooltip = ""
    if (!checkToolbarAccess(ToolbarOptions.Edit_Flightplans)) {
        // shouldn't be possible since route protected as well
        isDeleteDisabled = true
        deleteTooltip = "Missing required permissions"
    } else if (flightPlannedLines.some((line) => !!line.mission_name)) {
        isDeleteDisabled = true
        deleteTooltip = "Flown missions must be deleted before deleting a flightplan"
    }

    const [submitting, setSubmitting] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [completeModalOpen, setCompleteModalOpen] = useState(false)
    const selectedModes = useFlightplanEditStore((state) => state.selectedModes)

    // Load everything before we stop showing the loading bar
    const loading =
        acquisitionModesResponse.isLoading ||
        acquisitionModesForFlightPlanResponse.isLoading ||
        fetchingFlightplans ||
        selectingFlightplan ||
        parsingFile

    useEffect(() => {
        if (acquisitionModesResponse.error?.message != null) {
            setError(acquisitionModesResponse.error?.message)
        }
    }, [acquisitionModesResponse.error?.message])

    useEffect(() => {
        if (acquisitionModesForFlightPlanResponse.error?.message != null) {
            setError(acquisitionModesForFlightPlanResponse.error?.message)
        }
    }, [acquisitionModesForFlightPlanResponse.error?.message])

    // First use effect checks if we need to reset the store. Reset the store if project number changes.
    // Do not reset if this is create flightplan, since the parsed file set necessary data
    useEffect(() => {
        if (isEditFlow) {
            resetFlightplansStore()
        }
    }, [isEditFlow, projectNumber])

    // If Edit mode and project number changes, fetch projects and select a flightplan
    useEffect(() => {
        if (isEditFlow) {
            // project number changed, find all the flightplans for the project
            const fetch = async () => {
                const flightplans = await fetchFlightplansForProject(projectNumber!)
                if (flightplans.length > 0) {
                    if (flightplanIdNum != null) {
                        const flightplan = flightplans.find(
                            (flightplanOfProj) => flightplanOfProj.flight_plan_id === flightplanIdNum,
                        )
                        if (flightplan != null) {
                            // Selecting flightplan with ID from URL
                            await selectFlightplanForProject(flightplan)
                        }
                    } else {
                        // Selecting the first flightplan in the list, b/c none in URL
                        await selectFlightplanForProject(flightplans[0])
                    }
                }
            }
            fetch()
        }
    }, [isEditFlow, projectNumber])

    // If edit mode and flightplanId changes, select that flightplan
    useEffect(() => {
        if (isEditFlow) {
            const fetch = async () => {
                if (flightplanIdNum != null) {
                    const flightplanFromPlans = flightplansForProject.find(
                        (flightplanOfProj) => flightplanOfProj.flight_plan_id === flightplanIdNum,
                    )
                    if (flightplanFromPlans != null) {
                        await selectFlightplanForProject(flightplanFromPlans)
                    }
                }
            }
            fetch()
        }
    }, [isEditFlow, flightplanIdNum])

    const getUnflownLineIds = () => {
        return flightPlannedLines.filter((fl) => Boolean(!fl.mission_name)).map((fl) => fl.planned_line_id)
    }

    /**
     * Navigate to confirmation page after saving flightplan name.
     */
    const handleConfirmation = () => {
        nexusNavigate.confirmationPage(
            isEditFlow ? ConfirmationRoutes.FLIGHTPLAN_EDITED : ConfirmationRoutes.FLIGHTPLAN_SUBMITTED,
            {
                projectNumber,
                entityName: selectedFlightplan.flight_plan_name,
            },
        )
    }

    /**
     * Attempt to submit changes, or go back.
     */
    const handleStepChange = async (newStep: number) => {
        if (newStep === -1) {
            // return to upload screen, or return home in edit flow
            if (isEditFlow) {
                nexusNavigate.goHome()
            } else {
                navigate(`/${projectNumber}${routes.flightplanUpload}`)
            }
        } else {
            setError(undefined)
            setSubmitting(true)

            // save flightplan
            const unflownLines = selectedFlightplan.status === FlightplanStatus.Complete ? getUnflownLineIds() : []

            const onCompletionConfiguration: MutateOptions<void, Error, unknown> = {
                onSuccess: () => {
                    handleConfirmation()
                },
                onError: (error) => {
                    setError(error.message)
                },
                onSettled: () => {
                    setSubmitting(false)
                },
            }

            if (isEditFlow) {
                updateFlightplan.mutate(
                    {
                        ...selectedFlightplan,
                        deleted_lines: [...deletedPlannedLineIds, ...unflownLines],
                        acquisition_modes: selectedModes,
                    },
                    onCompletionConfiguration,
                )
            } else {
                const flight_planned_lines: FlightPlannedLineCreateDto[] = flightPlannedLines.map((line) => {
                    const newLine: FlightPlannedLineCreateDto = { ...line }
                    // If row is inserted than assure temp planned_line_id is removed
                    if (isRowInserted(line.planned_line_id)) {
                        newLine.planned_line_id = undefined
                    }
                    newLine.node_count = undefined
                    return newLine
                })

                createFlightplan.mutate(
                    {
                        flight_plan: selectedFlightplan,
                        acquisition_modes: selectedModes,
                        flight_planned_lines,
                    },
                    onCompletionConfiguration,
                )
            }
        }
    }

    const handleSelectedFlightplanChange = async (value: FlightplanType) => {
        navigate(`/${projectNumber}${routes.flightplanEditor}/edit/${value.flight_plan_id}`)
    }

    const handleDelete = async () => {
        setDeleteModalOpen(false)
        setDeleting(true)

        if (flightplanIdNum != null) {
            deleteFlightplan.mutate(flightplanIdNum, {
                onSuccess: () => {
                    nexusNavigate.goHome()
                },
                onError: (error) => {
                    setError(error.message)
                },
                onSettled: () => {
                    setDeleting(false)
                },
            })
        }
    }

    const unflownLineCount = getUnflownLineIds().length

    return (
        <StepperForm
            errorMessages={[error, flightPlanStoreFetchError]}
            onErrorClose={async () => {
                setError(undefined)
                await flightPlanStoreClearError()
            }}
            warnings={warnings}
            loading={loading}
            submitting={submitting}
            empty={isFlightplanEmpty}
            emptyText="No flightplans found for this project."
            validationProps={{
                model: getFlightplanValidatorModel(selectedFlightplan, flightPlannedLines, selectedModes),
                dependencies: [selectedFlightplan, flightPlannedLines, selectedModes],
            }}
            onStepChange={handleStepChange}
            header={
                <Stack>
                    <ProjectTitle projectNumber={projectNumber} />
                    {isEditFlow && (
                        <Stack>
                            <Label gutterBottom="sm">Select a Flightplan to Edit</Label>
                            <Stack>
                                <NexAutocomplete
                                    testId={"flightplan-edit-select"}
                                    value={selectedFlightplan}
                                    options={flightplansForProject}
                                    getOptionLabel={(opt: FlightplanType) =>
                                        StringUtils.stringOrEmpty(opt.flight_plan_name)
                                    }
                                    renderOption={(props, plan) => (
                                        <li
                                            {...props}
                                            key={plan.flight_plan_id}
                                            data-testid={`select-fp-dropdown-${plan.flight_plan_name}`}
                                        >
                                            {plan.flight_plan_name}
                                        </li>
                                    )}
                                    onChange={(e, value) => value && handleSelectedFlightplanChange(value)}
                                    width="md"
                                    disableClearable={true}
                                    disabled={loading}
                                    inline
                                    sx={{ mb: 2 }}
                                />
                            </Stack>
                        </Stack>
                    )}
                    <AlertModal
                        danger
                        open={deleteModalOpen}
                        pending={deleting}
                        title="Delete Flightplan?"
                        onConfirm={handleDelete}
                        onClose={() => setDeleteModalOpen(false)}
                    >
                        Are you sure you want to delete this flightplan? This action cannot be reversed.
                    </AlertModal>
                    <AlertModal
                        danger={unflownLineCount > 0}
                        open={completeModalOpen}
                        title="Delete Unflown Lines?"
                        confirmText={unflownLineCount > 0 ? "Confirm" : "Yes"}
                        onConfirm={() => {
                            updateFlightplanField("status", FlightplanStatus.Complete)
                            setCompleteModalOpen(false)
                        }}
                        onClose={() => setCompleteModalOpen(false)}
                    >
                        Changing flightplan status to "Complete" will cause{" "}
                        <strong>{unflownLineCount} unflown lines</strong> to be permanently deleted.
                        <p>
                            {unflownLineCount > 0
                                ? "This cannot be undone. Are you sure you want to perform this action?"
                                : "Proceed?"}
                        </p>
                    </AlertModal>
                </Stack>
            }
            disableSubmit={deleting || loading || (isEditFlow && flightplanIdNum == null)}
            secondaryAction={
                isEditFlow && (
                    <Tooltip arrow title={!isFlightplanEmpty && isDeleteDisabled ? deleteTooltip : ""}>
                        <span className={classes.delete_tooltip}>
                            <LoadingButton
                                onClick={() => setDeleteModalOpen(true)}
                                pending={deleting}
                                disabled={
                                    isFlightplanEmpty ||
                                    loading ||
                                    isDeleteDisabled ||
                                    submitting ||
                                    flightplanIdNum == null
                                }
                                variant="outlined"
                                color="primary"
                                size="large"
                                className={classes.btn_delete}
                            >
                                Delete
                            </LoadingButton>
                        </span>
                    </Tooltip>
                )
            }
        >
            <Stack>
                <Label gutterBottom="sm">Flightplan Name</Label>
                <TextField
                    value={selectedFlightplan.flight_plan_name ?? ""}
                    onChange={(e) => updateFlightplanField("flight_plan_name", e.target.value ?? undefined)}
                    variant="outlined"
                    helperText="Required"
                    className={cx(classes.input__md, classes.gutterBottom__md)}
                    slotProps={{
                        htmlInput: {
                            "data-testid": "flightplan-name-input",
                        },
                    }}
                    error={
                        selectedFlightplan.flight_plan_name == null ||
                        selectedFlightplan.flight_plan_name.trim().length === 0
                    }
                />
                <Label gutterBottom="sm">Acquisition Platform</Label>
                <NexSelect
                    data-testid={"flightplan-acquisition-platform-select"}
                    value={selectedFlightplan?.acquisition_platform ?? ""}
                    onChange={(e) => {
                        if (e.target.value) {
                            updateFlightplanField("acquisition_platform", e.target.value as AcquisitionPlatform)
                        } else {
                            updateFlightplanField("acquisition_platform", undefined)
                        }
                    }}
                    options={keysForStringEnum(AcquisitionPlatform)}
                    helperText="Required"
                    error={selectedFlightplan?.acquisition_platform == null}
                />
                <FlightplanCompletedLines />
                <FlightplanStatusSelect
                    onChange={(status?: FlightplanStatus) => {
                        if (status === FlightplanStatus.Complete) {
                            setCompleteModalOpen(true)
                            return
                        }
                        updateFlightplanField("status", status)
                    }}
                    isEditFlow={isEditFlow}
                />
                <Box mb={2}>
                    <FlightplanEditorSelectAcquisitionModes flightplanId={flightplanIdNum} />
                </Box>
                <FlightplanTable />
            </Stack>
        </StepperForm>
    )
}
